/**
 * Orders Management Page
 *
 * Dedicated screen for managing order forms and viewing orders.
 * Includes order list, order details, payment settings, and custom domain configuration.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import type { OrderSettings } from '../types/form';

interface OrderForm {
  id: string;
  name: string;
  slug: string;
  description?: string;
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
  schema?: {
    settings?: {
      order?: OrderSettings;
    };
  };
}

interface Order {
  id: string;
  order_number: string;
  customer_email: string;
  customer_name?: string;
  status: 'pending' | 'processing' | 'completed' | 'cancelled' | 'refunded';
  total: number;
  currency: string;
  created_at: string;
  items: OrderItem[];
}

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

export default function OrdersPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'orders' | 'settings' | 'shipping' | 'domain'>('orders');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Order form settings
  const [orderSettings, setOrderSettings] = useState<OrderSettings>({
    enabled: true,
    orderNumberPrefix: 'ORD-',
    orderNumberStart: 1001,
    requirePayment: true,
    paymentMethods: ['stripe'],
    collectShippingAddress: true,
    collectBillingAddress: false,
    guestCheckout: true,
    sendOrderConfirmation: true,
  });

  // Load order form and orders
  useEffect(() => {
    async function loadData() {
      if (!id) return;

      try {
        setLoading(true);

        // Load order form settings
        const formResponse = await api.get(`/api/forms/${id}`);
        const formData = await formResponse.json() as OrderForm;
        setOrderForm(formData);

        if (formData.schema?.settings?.order) {
          setOrderSettings(formData.schema.settings.order);
        }

        // Load orders (submissions)
        try {
          const ordersResponse = await api.get(`/api/forms/${id}/submissions?limit=50`);
          const submissionsData = await ordersResponse.json() as { submissions: Array<{ id: string; data: Record<string, unknown>; created_at: string }> };

          // Transform submissions to orders
          const transformedOrders: Order[] = submissionsData.submissions.map((sub, index) => ({
            id: sub.id,
            order_number: `${orderSettings.orderNumberPrefix}${(orderSettings.orderNumberStart || 1001) + index}`,
            customer_email: (sub.data.email as string) || 'N/A',
            customer_name: (sub.data.name as string) || undefined,
            status: 'pending' as const,
            total: (sub.data.total as number) || 0,
            currency: orderSettings.shippingMethods?.[0]?.price ? 'USD' : 'USD',
            created_at: sub.created_at,
            items: [],
          }));
          setOrders(transformedOrders);
        } catch {
          // No orders yet, that's okay
          setOrders([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load order form');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  // Save order form settings
  const handleSave = useCallback(async () => {
    if (!id || !orderForm) return;

    try {
      setSaving(true);
      await api.put(`/api/forms/${id}`, {
        schema: {
          ...orderForm.schema,
          settings: {
            ...orderForm.schema?.settings,
            order: orderSettings,
          },
        },
      });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save order form');
    } finally {
      setSaving(false);
    }
  }, [id, orderForm, orderSettings]);

  // Publish order form
  const handlePublish = useCallback(async () => {
    if (!id) return;

    try {
      setSaving(true);
      await api.post(`/api/forms/${id}/publish`);
      setOrderForm(prev => prev ? { ...prev, status: 'published' } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish order form');
    } finally {
      setSaving(false);
    }
  }, [id]);

  // Update order status
  const handleUpdateOrderStatus = useCallback(async (orderId: string, newStatus: Order['status']) => {
    try {
      // In a real implementation, this would update the submission status
      setOrders(prev => prev.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update order status');
    }
  }, []);

  // Get status badge color
  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'processing': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'cancelled': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
      case 'refunded': return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  if (error && !orderForm) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={() => navigate('/formspace')}
          className="mt-4 text-pink-600 hover:underline"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!orderForm) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Order form not found</p>
        <button
          onClick={() => navigate('/formspace')}
          className="mt-4 text-pink-600 hover:underline"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/formspace"
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-pink-100 dark:bg-pink-900/50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-pink-600 dark:text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{orderForm.name}</h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Order Form
                    {orderForm.status === 'published' && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 rounded-full">
                        Published
                      </span>
                    )}
                    {orderForm.status === 'draft' && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400 rounded-full">
                        Draft
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={handlePublish}
              disabled={saving || orderForm.status === 'published'}
              className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50"
            >
              {orderForm.status === 'published' ? 'Published' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-4">
          {(['orders', 'settings', 'shipping', 'domain'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-pink-500 text-pink-600 dark:text-pink-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'orders' && orders.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-400 rounded-full">
                  {orders.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Orders</h3>
              <div className="flex gap-2">
                <button className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                  Export CSV
                </button>
              </div>
            </div>

            {orders.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">No orders yet</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Orders will appear here once customers start purchasing
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Order #</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Customer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Total</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                        onClick={() => setSelectedOrder(order)}
                      >
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                          {order.order_number}
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm text-gray-900 dark:text-white">{order.customer_name || 'Guest'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{order.customer_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                          {order.currency} {order.total.toFixed(2)}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(order.status)}`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <select
                            value={order.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleUpdateOrderStatus(order.id, e.target.value as Order['status']);
                            }}
                            className="text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1 dark:bg-gray-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="refunded">Refunded</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="p-6 space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Order Form Settings</h3>

            {/* Order Numbers */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">Order Numbers</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Order Prefix
                  </label>
                  <input
                    type="text"
                    value={orderSettings.orderNumberPrefix}
                    onChange={(e) => setOrderSettings({ ...orderSettings, orderNumberPrefix: e.target.value })}
                    className="w-full input"
                    placeholder="ORD-"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Starting Number
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={orderSettings.orderNumberStart}
                    onChange={(e) => setOrderSettings({ ...orderSettings, orderNumberStart: parseInt(e.target.value) || 1 })}
                    className="w-full input"
                  />
                </div>
              </div>
            </div>

            {/* Payment */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">Payment</h4>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={orderSettings.requirePayment}
                  onChange={(e) => setOrderSettings({ ...orderSettings, requirePayment: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-pink-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Require payment at checkout</span>
              </label>

              {orderSettings.requirePayment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Payment Methods
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(['stripe', 'paypal', 'cod', 'bank_transfer'] as const).map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => {
                          const current = orderSettings.paymentMethods || [];
                          if (current.includes(method)) {
                            setOrderSettings({ ...orderSettings, paymentMethods: current.filter(m => m !== method) });
                          } else {
                            setOrderSettings({ ...orderSettings, paymentMethods: [...current, method] });
                          }
                        }}
                        className={`px-4 py-2 text-sm rounded-lg border transition-all ${
                          orderSettings.paymentMethods?.includes(method)
                            ? 'border-pink-500 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {method === 'stripe' && 'Stripe'}
                        {method === 'paypal' && 'PayPal'}
                        {method === 'cod' && 'Cash on Delivery'}
                        {method === 'bank_transfer' && 'Bank Transfer'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Customer Options */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">Customer Options</h4>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={orderSettings.guestCheckout}
                  onChange={(e) => setOrderSettings({ ...orderSettings, guestCheckout: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-pink-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Allow guest checkout (no account required)</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={orderSettings.collectShippingAddress}
                  onChange={(e) => setOrderSettings({ ...orderSettings, collectShippingAddress: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-pink-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Collect shipping address</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={orderSettings.collectBillingAddress}
                  onChange={(e) => setOrderSettings({ ...orderSettings, collectBillingAddress: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-pink-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Collect billing address (separate from shipping)</span>
              </label>
            </div>

            {/* Notifications */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
              <h4 className="text-md font-medium text-gray-700 dark:text-gray-300">Notifications</h4>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={orderSettings.sendOrderConfirmation}
                  onChange={(e) => setOrderSettings({ ...orderSettings, sendOrderConfirmation: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-pink-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Send order confirmation email to customer</span>
              </label>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={orderSettings.sendShippingNotification}
                  onChange={(e) => setOrderSettings({ ...orderSettings, sendShippingNotification: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-pink-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Send shipping notification when order is shipped</span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notification Email (for new orders)
                </label>
                <input
                  type="email"
                  value={orderSettings.notificationEmail || ''}
                  onChange={(e) => setOrderSettings({ ...orderSettings, notificationEmail: e.target.value || undefined })}
                  className="w-full input"
                  placeholder="admin@example.com"
                />
              </div>
            </div>
          </div>
        )}

        {/* Shipping Tab */}
        {activeTab === 'shipping' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Shipping Methods</h3>
              <button className="px-4 py-2 border border-pink-500 text-pink-600 dark:text-pink-400 rounded-lg hover:bg-pink-50 dark:hover:bg-pink-900/20">
                + Add Shipping Method
              </button>
            </div>

            {(!orderSettings.shippingMethods || orderSettings.shippingMethods.length === 0) ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">No shipping methods configured</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Add shipping options for your customers
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {orderSettings.shippingMethods.map((method) => (
                  <div
                    key={method.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">{method.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{method.description}</p>
                      {method.estimatedDays && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Estimated: {method.estimatedDays}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900 dark:text-white">
                        ${method.price.toFixed(2)}
                      </p>
                      {method.isDefault && (
                        <span className="text-xs text-pink-600 dark:text-pink-400">Default</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Domain Tab */}
        {activeTab === 'domain' && (
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Custom Domain</h3>

            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">Connect Your Domain</h4>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                      Host your order form on your own domain by adding a CNAME record pointing to our servers.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Custom Domain
                </label>
                <input
                  type="text"
                  value={orderSettings.customDomain || ''}
                  onChange={(e) => setOrderSettings({ ...orderSettings, customDomain: e.target.value || undefined })}
                  className="w-full input"
                  placeholder="orders.yourdomain.com"
                />
              </div>

              {orderSettings.customDomain && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">DNS Configuration</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Add the following CNAME record to your domain's DNS settings:
                  </p>
                  <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Type:</span>
                      <span>CNAME</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Name:</span>
                      <span>{orderSettings.customDomain?.split('.')[0] || 'orders'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Value:</span>
                      <span>orders.xtform.app</span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    {orderSettings.cnameVerified ? (
                      <span className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Domain verified
                      </span>
                    ) : (
                      <>
                        <span className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Pending verification
                        </span>
                        <button className="text-sm text-pink-600 hover:underline">
                          Verify now
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Current Order Form URL</h4>
                <div className="flex items-center gap-2">
                  <code className="bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded text-sm text-gray-800 dark:text-gray-200 flex-1">
                    {window.location.origin}/order/{orderForm.slug}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/order/${orderForm.slug}`)}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title="Copy URL"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedOrder(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Order {selectedOrder.order_number}
              </h3>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedOrder.status)}`}>
                  {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Date</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {new Date(selectedOrder.created_at).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Customer</span>
                <div className="text-right">
                  <p className="text-sm text-gray-900 dark:text-white">{selectedOrder.customer_name || 'Guest'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{selectedOrder.customer_email}</p>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between font-medium">
                  <span className="text-gray-900 dark:text-white">Total</span>
                  <span className="text-lg text-gray-900 dark:text-white">
                    {selectedOrder.currency} {selectedOrder.total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
